/**
 * Machine-readable description of the public protocol (`system.describe`, GET /api/schema, /api/v1/schema), the whole
 * contract a client of any platform (Android, iOS, desktop) needs, as JSON Schema (draft 2020-12):
 *  - every request: the payload schema, generated from the same validators the server runs (./validators.ts via
 *    ./schema.ts toJsonSchema), the router metadata (auth, mutation, feature, paginated, deprecated) and the schema of
 *    its `result` (./contract.ts);
 *  - every event with the schema of its `payload`; the envelopes; the error codes and every stable error reason
 *    (./reasons.ts);
 *  - the player.command ops with their args, results and refusal reasons (./commands.ts);
 *  - the stored game state format `fwp-state-v1` with its value markers and decoding rules (./contract.ts).
 */
import { COMMAND_CATALOGUE } from './commands';
import { EVENT_PAYLOAD_SCHEMAS, GAME_STATE_FORMATS, RESULT_SCHEMAS } from './contract';
import { ERROR_CODES, ERROR_CODE_INFO } from './errors';
import { ERROR_REASONS, ERROR_REASON_CATALOGUE } from './reasons';
import { CORRELATION_ID_PATTERN, REQUEST_ID_PATTERN } from './envelope';
import {
  ALL_FEATURES,
  type ApiDescription,
  DEPRECATED_REQUESTS,
  EVENT_DESCRIPTIONS,
  EVENT_TYPES,
  type FeatureFlag,
  OPTIONAL_FEATURES,
  PAGINATED_REQUESTS,
  REQUEST_DESCRIPTIONS,
  REQUEST_META,
  REQUEST_TYPES,
  featureOf,
} from './messages';
import { JSON_SCHEMA_DIALECT, type JsonSchema, toJsonSchema } from './schema';
import { PAYLOAD_VALIDATORS } from './validators';
import { API_PATHS, PROTOCOL_VERSION, SUPPORTED_PROTOCOL_VERSIONS } from './version';

export interface DescribeOptions {
  serverVersion: string;
  publicBaseUrl: string | null;
  /** Enabled feature flags of the server. */
  enabledFeatures: readonly FeatureFlag[];
}

/** A fresh plain-JSON copy (the description is serialized and may be modified by its user). */
function copy<T>(v: T): T {
  return JSON.parse(JSON.stringify(v)) as T;
}

const ID_SCHEMA: JsonSchema = { type: 'string', pattern: REQUEST_ID_PATTERN.source };

/** JSON Schema of every request payload, keyed by request type (a fresh copy per call). */
export function requestPayloadSchemas(): Record<string, JsonSchema> {
  const out: Record<string, JsonSchema> = {};
  for (const type of REQUEST_TYPES) out[type] = toJsonSchema(PAYLOAD_VALIDATORS[type]);
  return out;
}

function envelopeSchemas(): ApiDescription['envelopes'] {
  const error: JsonSchema = {
    type: 'object',
    properties: {
      code: { enum: [...ERROR_CODES] },
      message: { type: 'string' },
      details: { description: 'Machine-readable details; `reason` is a stable code where the error has one' },
    },
    required: ['code', 'message'],
  };
  const meta: JsonSchema = {
    ts: { type: 'integer', description: 'Server time (epoch ms) when the response was sent' },
    correlationId: { type: 'string', pattern: CORRELATION_ID_PATTERN.source, description: "The request's correlation id, echoed" },
  };
  return {
    request: {
      type: 'object',
      properties: {
        v: { type: 'integer', enum: [...SUPPORTED_PROTOCOL_VERSIONS] },
        id: {
          ...ID_SCHEMA,
          description:
            'Request id; the idempotency key of mutations (resend a mutation whose reply was lost under the same id: the server keeps the reply of a successful mutation for 10 minutes, also across a server restart; player.command retries by its opId). Unique within the device session for at least 10 minutes and hard to guess (a random prefix per app start plus a counter, or a UUID)',
        },
        type: { enum: [...REQUEST_TYPES] },
        payload: { description: 'The payload of `type` (see requests[type].payload); default {}' },
        correlationId: { type: 'string', pattern: CORRELATION_ID_PATTERN.source, description: 'Optional tracing id (HTTP: also X-Correlation-Id)' },
      },
      required: ['v', 'id', 'type'],
      additionalProperties: false,
    },
    response: {
      oneOf: [
        {
          type: 'object',
          properties: { v: { type: 'integer' }, id: { type: 'string' }, ok: { const: true }, result: {}, ...meta },
          required: ['v', 'id', 'ok', 'result', 'ts'],
        },
        {
          type: 'object',
          properties: { v: { type: 'integer' }, id: { type: 'string' }, ok: { const: false }, error, ...meta },
          required: ['v', 'id', 'ok', 'error', 'ts'],
        },
      ],
    },
    event: {
      type: 'object',
      properties: {
        v: { type: 'integer' },
        event: { enum: [...EVENT_TYPES] },
        payload: {},
        seq: { type: 'integer', description: 'Per-player sequence number (strictly increasing); resume after it' },
        ts: { type: 'integer', description: 'Server time (epoch ms) of the event' },
      },
      required: ['v', 'event', 'payload', 'seq', 'ts'],
    },
  };
}

/** The API description document (see the module comment). */
export function describeApi(opts: DescribeOptions): ApiDescription {
  const payloads = requestPayloadSchemas();
  const paginated = new Set<string>(PAGINATED_REQUESTS);
  const requests: ApiDescription['requests'] = {};
  for (const type of REQUEST_TYPES) {
    const meta = REQUEST_META[type];
    const entry: ApiDescription['requests'][string] = {
      payload: { $schema: JSON_SCHEMA_DIALECT, ...payloads[type] },
      mutation: meta.mutating,
      auth: meta.auth,
      result: copy(RESULT_SCHEMAS[type]),
    };
    const description = REQUEST_DESCRIPTIONS[type];
    if (description) entry.description = description;
    const feature = featureOf(type);
    if (feature) entry.feature = feature;
    if (paginated.has(type)) entry.paginated = true;
    if (DEPRECATED_REQUESTS.includes(type)) entry.deprecated = true;
    requests[type] = entry;
  }
  return {
    $schema: JSON_SCHEMA_DIALECT,
    protocol: PROTOCOL_VERSION,
    supportedProtocols: [...SUPPORTED_PROTOCOL_VERSIONS],
    serverVersion: opts.serverVersion,
    publicBaseUrl: opts.publicBaseUrl,
    paths: { api: API_PATHS.api, versionedApi: API_PATHS.versionedApi, schema: API_PATHS.schema, ws: API_PATHS.ws, health: API_PATHS.health },
    envelopes: envelopeSchemas(),
    requests,
    events: EVENT_TYPES.map((event) => ({ event, description: EVENT_DESCRIPTIONS[event], payload: copy(EVENT_PAYLOAD_SCHEMAS[event]) })),
    errors: ERROR_CODES.map((code) => ({ code, ...ERROR_CODE_INFO[code] })),
    errorReasons: [...ERROR_REASONS],
    reasons: ERROR_REASON_CATALOGUE.map((r) => ({ ...r, codes: [...r.codes] })),
    commands: Object.fromEntries(Object.entries(COMMAND_CATALOGUE).map(([op, info]) => [op, copy(info)])) as ApiDescription['commands'],
    stateFormats: copy(GAME_STATE_FORMATS) as ApiDescription['stateFormats'],
    features: { all: [...ALL_FEATURES], optional: [...OPTIONAL_FEATURES], enabled: [...opts.enabledFeatures] },
  };
}
